import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { CircleNotch, CheckCircle, RocketLaunch, Warning, ArrowLeft, Paperclip, Scissors } from "@phosphor-icons/react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const fmt = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const CHANNEL = { cpa: ["bg-sky-50 text-sky-700 border-sky-200", "CPA review"], client: ["bg-amber-50 text-amber-700 border-amber-200", "Awaiting client"] };

function SplitEditor({ t, categories, onSplit, busy, onCancel }) {
  const orig = Number(t.amount);
  const [parts, setParts] = useState([
    { amount: "", category: t.category || "" },
    { amount: "", category: "Owner draws / distributions" },
  ]);
  const setPart = (i, k, v) => setParts((p) => p.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const total = parts.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const remaining = Math.round((orig - total) * 100) / 100;
  const ready = parts.every((p) => p.amount !== "" && !isNaN(Number(p.amount)) && p.category) && remaining === 0;
  return (
    <div className="rounded-md border border-indigo-200 bg-indigo-50/50 p-2.5 space-y-1.5" data-testid={`wb-split-editor-${t.id}`}>
      <p className="text-[11px] font-medium text-indigo-900">Split {fmt(t.amount)} into parts — totals must match to the penny (reconciliation preserved)</p>
      {parts.map((p, i) => (
        <div key={i} className="flex flex-wrap items-center gap-1.5">
          <Input data-testid={`wb-split-amount-${t.id}-${i}`} placeholder={`Part ${i + 1} amount (e.g. ${(orig / parts.length).toFixed(2)})`}
            value={p.amount} onChange={(e) => setPart(i, "amount", e.target.value)} className="h-7 w-40 text-xs bg-white" />
          <select data-testid={`wb-split-category-${t.id}-${i}`} className="h-7 rounded-md border border-zinc-200 bg-white px-2 text-xs"
            value={p.category} onChange={(e) => setPart(i, "category", e.target.value)}>
            <option value="">Category…</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {parts.length > 2 && <button onClick={() => setParts((x) => x.filter((_, idx) => idx !== i))} className="text-[10px] text-zinc-400 hover:text-rose-600">remove</button>}
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <button data-testid={`wb-split-add-part-${t.id}`} onClick={() => setParts((p) => [...p, { amount: "", category: "" }])} className="text-[10px] text-indigo-700 hover:underline">+ Add part</button>
        <span className={`text-[10px] font-mono ${remaining === 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid={`wb-split-remaining-${t.id}`}>
          {remaining === 0 ? "Balanced ✔" : `Remaining: ${fmt(remaining)}`}
        </span>
        <Button data-testid={`wb-split-submit-${t.id}`} size="sm" className="h-6 text-[11px]" disabled={!ready || busy}
          onClick={() => onSplit({ txn_id: t.id, parts: parts.map((p) => ({ amount: p.amount, category: p.category })) })}>
          Split transaction
        </Button>
        <button onClick={onCancel} className="text-[10px] text-zinc-500 hover:text-zinc-800">Cancel</button>
      </div>
    </div>
  );
}

function ReceiptPreview({ r }) {
  const url = `${API_BASE}/api/pnl/receipts/${r.id}`;
  const isImg = /\.(png|jpe?g|webp|gif|heic)$/i.test(r.filename);
  return (
    <div className="rounded-md border border-zinc-200 bg-white p-2 space-y-1" data-testid={`wb-receipt-preview-${r.id}`}>
      <div className="flex items-center justify-between text-[10px] text-zinc-500">
        <span>{r.filename}</span>
        <a href={url} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline">Open in new tab ↗</a>
      </div>
      {isImg
        ? <img src={url} alt={r.filename} className="max-h-72 rounded border border-zinc-100 object-contain" />
        : <iframe src={url} title={r.filename} className="w-full h-72 rounded border border-zinc-100" />}
    </div>
  );
}

function ReviewRow({ t, categories, onApprove, onSplit, busy }) {
  const [cat, setCat] = useState(t.category || "");
  const [saveRule, setSaveRule] = useState(false);
  const [splitting, setSplitting] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const [ch, chLabel] = CHANNEL[t.review_channel] || CHANNEL.cpa;
  return (
    <div className="rounded-md border border-zinc-200 bg-white p-2.5 space-y-1.5" data-testid={`wb-review-row-${t.id}`}>
      <div className="flex items-center justify-between gap-2 text-xs">
        <div className="min-w-0">
          <span className="text-zinc-800">{t.description}</span>
          <span className="text-zinc-400"> · {t.txn_date} · {t.account_name}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="font-mono text-zinc-700">{fmt(t.amount)}</span>
          <span className={`text-[9px] border rounded-sm px-1.5 py-0.5 ${ch}`}>{chLabel}</span>
          {t.confidence != null && <span className="text-[10px] text-zinc-400">{t.confidence}%</span>}
        </div>
      </div>
      {t.rule && <p className="text-[10px] text-zinc-400">{t.rule}</p>}
      {(t.receipts || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {t.receipts.map((r) => (
            <button key={r.id} data-testid={`wb-receipt-link-${r.id}`} onClick={() => setPreviewId(previewId === r.id ? null : r.id)}
              className={`flex items-center gap-1 text-[10px] border rounded-sm px-1.5 py-0.5 ${previewId === r.id ? "text-white bg-emerald-600 border-emerald-600" : "text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100"}`}>
              <Paperclip size={11} /> {r.filename}
            </button>
          ))}
        </div>
      )}
      {previewId && <ReceiptPreview r={t.receipts.find((r) => r.id === previewId)} />}
      {splitting ? (
        <SplitEditor t={t} categories={categories} busy={busy} onCancel={() => setSplitting(false)}
          onSplit={(payload) => { onSplit(payload); setSplitting(false); }} />
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <select data-testid={`wb-category-select-${t.id}`} className="h-7 rounded-md border border-zinc-200 bg-white px-2 text-xs" value={cat} onChange={(e) => setCat(e.target.value)}>
            <option value="">Select category…</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <label className="flex items-center gap-1 text-[10px] text-zinc-500 cursor-pointer">
            <input type="checkbox" data-testid={`wb-save-rule-${t.id}`} checked={saveRule} onChange={(e) => setSaveRule(e.target.checked)} />
            Save as permanent rule
          </label>
          <Button data-testid={`wb-approve-btn-${t.id}`} size="sm" className="h-7 text-xs" disabled={!cat || busy} onClick={() => onApprove({ txn_id: t.id, category: cat, save_rule: saveRule })}>
            <CheckCircle size={13} className="mr-1" /> Approve
          </Button>
          <button data-testid={`wb-split-btn-${t.id}`} onClick={() => setSplitting(true)} disabled={busy}
            className="flex items-center gap-1 text-[11px] text-indigo-700 border border-indigo-200 rounded-md px-2 py-1 hover:bg-indigo-50 disabled:opacity-50">
            <Scissors size={12} /> Split
          </button>
        </div>
      )}
    </div>
  );
}

export function PnlWorkbench({ engId, onClose }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState("review");
  const { data: w } = useQuery({ queryKey: ["pnl-workbench", engId], queryFn: () => api.get(`/pnl/engagements/${engId}/workbench`).then((r) => r.data) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["pnl-workbench", engId] }); qc.invalidateQueries({ queryKey: ["pnl-console"] }); };
  const review = useMutation({
    mutationFn: (item) => api.post(`/pnl/engagements/${engId}/review`, { items: [item] }).then((r) => r.data),
    onSuccess: (d) => { toast.success(d.remaining_review_items ? `Approved — ${d.remaining_review_items} item(s) left` : "All items cleared — ready to publish"); refresh(); },
    onError: (e) => toast.error(errText(e)),
  });
  const split = useMutation({
    mutationFn: (payload) => api.post(`/pnl/engagements/${engId}/split`, payload).then((r) => r.data),
    onSuccess: (d) => { toast.success(`Split into ${d.created} lines — totals preserved to the penny`); refresh(); },
    onError: (e) => toast.error(errText(e)),
  });
  const publish = useMutation({
    mutationFn: () => api.post(`/pnl/engagements/${engId}/publish`).then((r) => r.data),
    onSuccess: () => { toast.success("P&L published to the client portal — period locked"); refresh(); },
    onError: (e) => toast.error(errText(e)),
  });
  if (!w) return <div className="p-6 text-center"><CircleNotch className="animate-spin text-zinc-400 mx-auto" size={20} /></div>;
  const e = w.engagement;
  const cpaItems = w.needs_review.filter((t) => t.review_channel === "cpa");
  const clientItems = w.needs_review.filter((t) => t.review_channel === "client");
  const canPublish = w.needs_review.length === 0 && !e.locked && w.cleared_count > 0;
  return (
    <div className="mt-3 rounded-lg border border-zinc-300 bg-zinc-50 p-4 space-y-3" data-testid="pnl-workbench">
      <div className="flex items-center justify-between">
        <button data-testid="wb-close-btn" onClick={onClose} className="text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1"><ArrowLeft size={12} /> Console</button>
        <div className="text-xs text-zinc-600">
          <strong className="text-zinc-900">{w.client_name}</strong> · {e.name} · Form {e.entity_type} · TY {e.tax_year}
          <span className="ml-2 text-emerald-700 font-medium" data-testid="wb-auto-pct">Auto-reconciled: {w.auto_pct}%</span>
          {e.locked && <span className="ml-2 text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm px-1.5 py-0.5">PUBLISHED · LOCKED</span>}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
        {/* Main workbench */}
        <div className="lg:col-span-3 space-y-2">
          <div className="flex gap-1">
            {[["review", `Needs Review (${w.needs_review.length})`], ["cleared", `Cleared (${w.cleared_count})`], ["rules", `Rules (${w.rules.length})`]].map(([k, label]) => (
              <button key={k} data-testid={`wb-tab-${k}`} onClick={() => setTab(k)}
                className={`text-xs px-3 py-1.5 rounded-md border ${tab === k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-100"}`}>
                {label}
              </button>
            ))}
          </div>
          {tab === "review" && (
            <div className="space-y-2">
              {clientItems.length > 0 && (
                <p className="text-[11px] text-amber-700 flex items-center gap-1"><Warning size={13} /> {clientItems.length} item(s) awaiting client answers — you can override them here if needed.</p>
              )}
              {!w.needs_review.length && <p className="text-xs text-zinc-400 italic py-3">No open items — everything is cleared.</p>}
              {[...cpaItems, ...clientItems].map((t) => <ReviewRow key={t.id} t={t} categories={w.categories} onApprove={(item) => review.mutate(item)} onSplit={(payload) => split.mutate(payload)} busy={review.isPending || split.isPending || e.locked} />)}
            </div>
          )}
          {tab === "cleared" && (
            <div className="rounded-md border border-zinc-200 bg-white divide-y divide-zinc-50 max-h-96 overflow-y-auto" data-testid="wb-cleared-list">
              {w.cleared.map((t) => (
                <div key={t.id} className="flex items-center justify-between px-2.5 py-1.5 text-[11px]">
                  <span className="text-zinc-700 truncate">{t.txn_date} · {t.description}</span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className="font-mono text-zinc-600">{fmt(t.amount)}</span>
                    <span className="text-zinc-400">{t.category} · {t.confidence}% · {t.source}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
          {tab === "rules" && (
            <div className="rounded-md border border-zinc-200 bg-white divide-y divide-zinc-50" data-testid="wb-rules-list">
              {!w.rules.length && <p className="text-xs text-zinc-400 italic p-3">No learned vendor rules yet.</p>}
              {w.rules.map((r) => (
                <div key={r.vendor_key} className="flex items-center justify-between px-2.5 py-1.5 text-[11px]">
                  <span className="text-zinc-700">“{r.vendor_key}” → <strong>{r.category}</strong>{r.note ? <span className="text-zinc-400"> · {r.note}</span> : null}</span>
                  <span className="text-zinc-400">{(r.created_at || "").slice(0, 10)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Live P&L preview panel */}
        <div className="lg:col-span-2 space-y-2">
          <div className="rounded-md border border-zinc-200 bg-white p-3" data-testid="wb-pnl-summary">
            <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1.5">Live P&L preview</p>
            {w.pnl_summary ? (
              <div className="text-xs space-y-1">
                <p className="flex justify-between"><span className="text-zinc-500">Gross income</span><span className="font-mono">{fmt(w.pnl_summary.gross_income)}</span></p>
                <p className="flex justify-between font-medium"><span className="text-zinc-700">Net income (tax basis)</span><span className="font-mono" data-testid="wb-net-income">{fmt(w.pnl_summary.net_income)}</span></p>
              </div>
            ) : <p className="text-xs text-zinc-400 italic">No categorized transactions yet.</p>}
          </div>
          <div className="rounded-md border border-zinc-200 bg-white p-3" data-testid="wb-balance-checks">
            <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1.5">Balance checks</p>
            {!w.recon.length && <p className="text-xs text-zinc-400 italic">Reconciliation runs on quote acceptance.</p>}
            {w.recon.map((r) => (
              <p key={r.account + r.filename} className="text-[11px] flex justify-between py-0.5">
                <span className="text-zinc-700">{r.account}</span>
                <span className={r.status === "passed" ? "text-emerald-600" : "text-rose-600"}>{r.status === "passed" ? `Balanced (Δ $0.00) ✔` : `Δ ${fmt(r.variance)}`}</span>
              </p>
            ))}
          </div>
          <div className="rounded-md border border-zinc-200 bg-white p-3" data-testid="wb-audit-log">
            <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1.5">Processing audit log</p>
            <p className="text-[11px] text-zinc-600 flex justify-between"><span>Lines processed</span><span className="font-mono">{w.audit_log.lines_processed}</span></p>
            <p className="text-[11px] text-zinc-600 flex justify-between"><span>Duplicates excluded</span><span className="font-mono">{w.audit_log.duplicates_excluded}</span></p>
            {w.audit_log.exclusions.map((x) => (
              <p key={x.category} className="text-[11px] text-zinc-500 flex justify-between"><span>{x.count} × {x.reason}</span><span className="font-mono">{fmt(x.total)}</span></p>
            ))}
          </div>
          <Button data-testid="wb-publish-btn" className="w-full" disabled={!canPublish || publish.isPending} onClick={() => publish.mutate()}>
            {publish.isPending ? <CircleNotch size={14} className="animate-spin mr-1" /> : <RocketLaunch size={14} className="mr-1" />}
            {e.locked ? "Published & locked" : "Approve & Publish to Client Portal"}
          </Button>
          {!canPublish && !e.locked && <p className="text-[10px] text-zinc-400 text-center">Publishing unlocks once every open item is cleared.</p>}
        </div>
      </div>
    </div>
  );
}
